import React, {useEffect, useRef, useState} from 'react';
import Document from "../../../models/documentModel";
import {ResponseData} from '@dvrd/fetch';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {createErrorMessage} from "../../../util/utils";
import {ActivateHandle} from '../../../util/interfaces';
import {DocumentType} from "../../../util/enums";
import DocumentsPage from "./documentsPage";
import DocumentDataController from "./documentsData/documentDataController";

type LoadConfig = {search?: string; active?: boolean;}

export default function DocumentsPageController() {
    const [documents, setDocuments] = useState<Array<Document>>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [active, setActive] = useState(false);
    const dataRef = useRef<ActivateHandle<Document>>(null);

    function onEditDocument(document: Document) {
        return function(evt: React.MouseEvent) {
            evt.stopPropagation();
            dataRef.current?.activate(document);
        }
    }

    function onAddDocument(documentType: DocumentType) {
        return function() {
            dataRef.current?.activate(new Document({document_type: documentType}));
        }
    }

    function onChangeSearch(value: string) {
        setSearch(value);
    }

    function onSearch() {
        loadDocuments();
    }

    function onChangeActive(value: boolean) {
        setActive(value);
        loadDocuments({active: value});
    }

    function onDownloadDocument(document: Document) {
        return function(evt: React.MouseEvent) {
            evt.stopPropagation();
            setLoading(true);
            document.download((_: Document, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (!success) showDialog(
                    createErrorMessage(data.message ?? 'Het downloaden van het bestand is niet gelukt.'),
                    'Downloaden mislukt');
            })
        }
    }

    function onDeleteDocument(document: Document) {
        return function(evt: React.MouseEvent) {
            evt.stopPropagation();
            showDialog(`Weet je zeker dat je het document '${document.label}' wilt verwijderen?`,
                'Document verwijderen',
                ['Nee', {label: 'Ja', onClick: onConfirmDeleteDocument(document), primary: false}]);
        }
    }

    function onConfirmDeleteDocument(document: Document) {
        return function() {
            setLoading(true);
            document.delete((document: Document, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    showSnackbar('Het document is verwijderd');
                    const _documents = documents.slice();
                    for (let ix = 0; ix < _documents.length; ix++) {
                        if (_documents[ix].id === document.id) {
                            _documents.splice(ix, 1);
                            setDocuments(_documents);
                            return;
                        }
                    }
                } else showDialog(
                    createErrorMessage(data.message ?? 'Het verwijderen van het document is niet gelukt.'),
                    'Verwijderen mislukt');
            })
        }
    }

    function onDocumentChanged(document: Document, isNew: boolean, isDeleted: boolean) {
        if (isNew) setDocuments(documents.toSpliced(0, 0, document));
        else {
            for (let ix = 0; ix < documents.length; ix++) {
                if (documents[ix].id === document.id) {
                    if (isDeleted) setDocuments(documents.toSpliced(ix, 1));
                    else setDocuments(documents.toSpliced(ix, 1, document));
                    return;
                }
            }
        }
    }

    function loadDocuments(params?: LoadConfig) {
        setLoading(true);
        const _search = params?.search ?? search;
        const _active = params?.active || undefined;
        Document.getAll({search: _search, active: _active},
            (documents: Array<Document>, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) setDocuments(documents);
                else showDialog(createErrorMessage(data.message ?? 'Het laden van de documenten is niet gelukt.'),
                    'Laden mislukt');
            })
    }

    useEffect(() => {
        loadDocuments();
    }, []);

    return (
        <>
            <DocumentsPage onChangeSearch={onChangeSearch} onSearch={onSearch} onChangeActive={onChangeActive}
                           onAddDocument={onAddDocument} onEditDocument={onEditDocument}
                           onDownloadDocument={onDownloadDocument} onDeleteDocument={onDeleteDocument}
                           documents={documents} loading={loading} search={search} active={active}/>
            <DocumentDataController ref={dataRef} onDocumentChanged={onDocumentChanged}/>
        </>
    );
}